import axiosClient from './axiosClient';
import {
  Tenant,
  TenantCreatePayload,
  TenantUpdatePayload,
  TenantVersion,
} from '../types/tenant';

export const tenantApi = {
  getAll: async (): Promise<Tenant[]> => {
    const { data } = await axiosClient.get('/api/tenant');
    return data;
  },

  getByCode: async (tenantCode: string): Promise<Tenant> => {
    const { data } = await axiosClient.get(`/api/tenant/${tenantCode}`);
    return data;
  },

  // Is a tenant code free to reuse? Reusable only when absent from BOTH the
  // platform tenant.tenant AND config.tenant. Used by create-from-ZIP to keep
  // the source tenant's code when it's not already taken on this platform.
  codeAvailability: async (
    code: string,
  ): Promise<{ code: string; onPlatform: boolean; inConfig: boolean; available: boolean }> => {
    const { data } = await axiosClient.get('/api/tenant/code-availability', { params: { code } });
    return data;
  },

  create: async (payload: TenantCreatePayload): Promise<Tenant> => {
    const { data } = await axiosClient.post('/api/tenant', payload);
    return data;
  },

  update: async (
    tenantCode: string,
    payload: TenantUpdatePayload,
  ): Promise<Tenant> => {
    const { data } = await axiosClient.put(
      `/api/tenant/${tenantCode}`,
      payload,
    );
    return data;
  },

  /** Super-admin only: toggle the env-local "prod tenant" flag (mig 245). */
  setProdFlag: async (
    tenantCode: string,
    isProdTenant: boolean,
  ): Promise<{
    tenant_code: string;
    tenant_name: string;
    is_prod_tenant: boolean;
    storyblokSpaceIdRetained?: boolean;
    storyblokSpaceIdExisting?: string;
    syncedToPlatform?: boolean;
  }> => {
    const { data } = await axiosClient.patch(
      `/api/tenant/${tenantCode}/prod-flag`,
      { isProdTenant },
    );
    return data;
  },

  /** Super-admin only: toggle the env-local "synthetic tenant" flag (mig 270). */
  setSyntheticFlag: async (
    tenantCode: string,
    isSynthetic: boolean,
  ): Promise<{ tenant_code: string; tenant_name: string; is_synthetic: boolean }> => {
    const { data } = await axiosClient.patch(
      `/api/tenant/${tenantCode}/synthetic-flag`,
      { isSynthetic },
    );
    return data;
  },

  /** The caller's effective access for a tenant (mig 246) — drives read-only gating. */
  myAccess: async (
    tenantCode: string,
  ): Promise<{ tenant_code: string; level: 'read' | 'write' | null; isSuperAdmin: boolean; enforced: boolean }> => {
    const { data } = await axiosClient.get(`/api/tenant/${tenantCode}/my-access`);
    return data;
  },

  getVersions: async (tenantCode: string): Promise<TenantVersion[]> => {
    const { data } = await axiosClient.get(
      `/api/tenant/${tenantCode}/versions`,
    );
    return data;
  },

  getVersion: async (
    tenantCode: string,
    version: number,
  ): Promise<Tenant> => {
    const { data } = await axiosClient.get(
      `/api/tenant/${tenantCode}/versions/${version}`,
    );
    return data;
  },

  rollback: async (
    tenantCode: string,
    version: number,
    versionedBy: string,
  ): Promise<Tenant> => {
    const { data } = await axiosClient.post(
      `/api/tenant/${tenantCode}/rollback`,
      { version, versioned_by: versionedBy },
    );
    return data;
  },

  exportConfig: async (tenantCode: string): Promise<unknown> => {
    const { data } = await axiosClient.get(`/api/tenant/${tenantCode}/export`);
    return data;
  },

  importConfig: async (payload: unknown): Promise<{ success: boolean; summary: string[] }> => {
    const { data } = await axiosClient.post('/api/tenant/import', payload);
    return data;
  },
};
